var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { useCallback, useMemo } from 'react';
import { useFormikContext } from 'formik';
export function useSearchContext() {
    const { values, setFieldValue, setFieldTouched, resetForm, touched, handleSubmit, isSubmitting } = useFormikContext();
    const { filters, modifiers } = values;
    const hasFilters = Boolean(Object.keys(filters || {}).length);
    const dirty = Boolean(touched.query || touched.filters || touched.columns || touched.orderBy);
    const activeFilters = useMemo(() => Object.entries(filters || {}).map(([key]) => key), [filters]);
    const clearModifiers = useCallback(() => {
        setFieldValue('modifiers', {});
        setFieldTouched('modifiers', true);
    }, [setFieldValue, setFieldTouched]);
    const setModifiers = useCallback((modifier) => {
        setFieldValue('modifiers', Object.assign(Object.assign({}, modifiers), modifier));
        setFieldTouched('modifiers', true);
    }, [setFieldValue, setFieldTouched, modifiers]);
    const clearFilters = useCallback(() => {
        setFieldValue('filters', {});
        setFieldTouched('filters', true);
    }, [setFieldValue, setFieldTouched]);
    const setOffset = useCallback((newOffset) => {
        setFieldValue('offset', newOffset);
        setFieldTouched('offset', false);
    }, [setFieldValue, setFieldTouched]);
    const setOrderBy = useCallback((orderBy) => {
        setFieldValue('orderBy', orderBy);
        setFieldTouched('orderBy', true);
    }, [setFieldValue, setFieldTouched]);
    const setHiddenColRows = useCallback((newColumnOrder) => {
        setFieldValue('columns', newColumnOrder);
        setFieldTouched('columns', true);
    }, [setFieldValue, setFieldTouched]);
    const setQuery = useCallback((newQuery) => {
        setFieldValue('query', newQuery);
        setFieldTouched('query', true);
    }, [setFieldValue, setFieldTouched]);
    const clearQuery = useCallback(() => {
        setFieldValue('query', '');
        setFieldTouched('query', true);
    }, [setFieldValue, setFieldTouched]);
    const removeFilter = useCallback((id) => {
        const _a = filters || {}, 
        // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
        _b = id, 
        // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
        _ = _a[_b], newFilterValues = __rest(_a, [typeof _b === "symbol" ? _b : _b + ""]);
        setFieldValue('filters', newFilterValues);
        setFieldTouched(`filters.${id}`, true);
    }, [setFieldValue, setFieldTouched, filters]);
    const toggleFilter = useCallback((id) => {
        const isAdded = activeFilters.includes(id);
        if (isAdded) {
            removeFilter(id);
        }
        else {
            setFieldValue('filters', Object.assign(Object.assign({}, filters), { [id]: undefined }));
            setFieldTouched(`filters.${id}`, false);
        }
        setFieldTouched('filters', true);
    }, [activeFilters, removeFilter, setFieldValue, setFieldTouched, filters]);
    return {
        values,
        hasFilters,
        activeFilters,
        modifiers,
        toggleFilter,
        removeFilter,
        clearFilters,
        setOffset,
        setOrderBy,
        setQuery,
        clearQuery,
        resetForm,
        setHiddenColRows,
        handleSubmit,
        setModifiers,
        clearModifiers,
        dirty,
        touched,
        isSubmitting,
    };
}
